import {
  Button,
  EmptyInput,
  errorsToPropertyErrors,
  Flex,
  FlexColumn, InputGroup, InputGroupBreak, InputRow,
  LockIcon,
  SecondaryButton,
  TextInput,
  ToastStatus,
  ToggleInput,
  useStyledTheme,
  useToastNotifications,
} from '@equifinance/shared';
import { PropsWithChildren, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { PaddedSection } from '../../../Account/components/Section';
import {
  AddBrokerUserPayload, BrokerUser, UpdateBrokerUserPayload,
} from '../../../../contexts/broker';
import UnlockIcon from '../../../Icons/UnlockIcon';
import { useManageUserContext } from '../../../../contexts/manageUser';

const BrokerUserContainer = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[3]};
  padding: ${theme.padding[6]};
  background-color: ${theme.colours.neutral[9]};
`}`;

const BrokerUserCotent = styled(FlexColumn)`${({ theme }) => `
  background-color: ${theme.colours.neutral[10]};
`}`;

const BreakLine = styled(InputGroupBreak)`
  margin: 0;
`;

const AdminLabel = styled.span`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[2]};
  margin-right: ${theme.padding[1]};
`}`;

const VerifyBtn = styled(Button)`
  width: fit-content;
  align-self: end;
`;

const GroupButtons = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[3]};
  align-self: flex-end;
`}`;

const bodyContentCss = (edit: boolean) => css`${({ theme }) => `
  background-color: ${edit ? theme.colours.tertiary[6] : theme.colours.neutral[10]};
`}`;

const headerCss = css`
  box-shadow: 0px 1px 2px 0px rgba(1, 50, 123, 0.15);
`;

function BrokerUserWrapper({ isLocked, isCreating, children }: PropsWithChildren<{ isLocked: boolean, isCreating: boolean }>) {
  const theme = useStyledTheme();
  return isCreating ? (<BrokerUserCotent>{children}</BrokerUserCotent>) : (
    <PaddedSection
      title="User details"
      level={2}
      titleIcon={isLocked ? <LockIcon /> : <UnlockIcon fill={theme.colours.neutral[5]} />}
      bodyCss={bodyContentCss(!isLocked)}
      headerCss={headerCss}
    >
      {children}
    </PaddedSection>
  );
}

export function BrokerUserDetails({
  data: brokerUser,
}: {
  data: BrokerUser,
}) {
  const [firstName, setFirstName] = useState(brokerUser.firstName ?? '');
  const [lastName, setLastName] = useState(brokerUser.surname ?? '');
  const [email, setEmail] = useState(brokerUser.emailAddress ?? '');
  const [telephoneNumber, setTelephoneNumber] = useState(brokerUser.telephoneNumber ?? '');
  const [admin, setAdmin] = useState(brokerUser.isAdmin ?? false);

  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [touch, setTouch] = useState(false);

  const {
    verifyUser, editingUserIds, deleteUser, unsetEditUser, updateUser,
  } = useManageUserContext();

  const { addToastNotification } = useToastNotifications();

  const showToastMessage = (text: string, type: ToastStatus) => {
    addToastNotification({
      text,
      type,
      showCloseIcon: true,
    });
  };

  const resetBrokerUser = () => {
    const {
      firstName, surname, telephoneNumber, emailAddress, isAdmin,
    } = brokerUser;

    setFirstName(firstName);
    setLastName(surname);
    setTelephoneNumber(telephoneNumber);
    setEmail(emailAddress);
    setAdmin(isAdmin);

    setErrors({});
  };

  const onCancel = () => {
    resetBrokerUser();
    unsetEditUser(brokerUser.id);
  };

  const isCreating = brokerUser?.status === 'OPEN';
  const isLocked = !editingUserIds.includes(brokerUser.id);
  const isEditMode = !isLocked;

  const canVerify = useMemo(() => (firstName && lastName && email), [firstName, lastName, email]);
  const ableToSave = firstName !== brokerUser.firstName
    || lastName !== brokerUser.surname
    || telephoneNumber !== brokerUser.telephoneNumber
    || admin !== brokerUser.isAdmin;

  const onVerify = async () => {
    setTouch(true);

    const user: AddBrokerUserPayload = {
      id: brokerUser.id,
      firstName,
      surname: lastName,
      companyId: brokerUser.companyId,
      isAdmin: admin,
      telephoneNumber,
      emailAddress: email,
    };

    try {
      await verifyUser(user);
      showToastMessage(`Email has been sent to the user, so that they can set up the password.
        Once the password has been set-up, the account will become active.`, ToastStatus.SUCCESS);
      // Delete temp user
      deleteUser(brokerUser.id);
    } catch (e: any) {
      setErrors(e?.response?.data?.errors ?? {});
      showToastMessage(`We are unable to send the verification link at this time.
        If the issue persists please contact the Sales Support team for assistance.`, ToastStatus.WARNING);
    }
  };

  const onSave = async () => {
    setTouch(true);

    const user: UpdateBrokerUserPayload = {
      id: brokerUser.id,
      firstName,
      surname: lastName,
      companyId: brokerUser.companyId,
      isAdmin: admin,
      telephoneNumber,
      emailAddress: email,
    };

    try {
      await updateUser(user);
      unsetEditUser(brokerUser.id);
      setErrors({});
    } catch (e: any) {
      const userId = e?.users?.[0]?.id ?? '';
      const errors = e?.errors?.[userId] ?? [];
      const userErrors = errorsToPropertyErrors(errors);

      setErrors(userErrors ?? {});
    }
  };

  return (
    <BrokerUserContainer>
      <BrokerUserWrapper isLocked={isLocked} isCreating={isCreating}>
        <InputGroup>
          <InputRow>
            <TextInput
              id="user-details-first-name"
              value={firstName}
              onChange={(v) => { setFirstName(v); }}
              label="First name"
              required
              disabled={!isCreating && isLocked}
              errors={errors.firstName ?? []}
              touchOverride={touch}
            />
            <TextInput
              id="user-details-last-name"
              value={lastName}
              onChange={(v) => { setLastName(v); }}
              label="Last name"
              required
              disabled={!isCreating && isLocked}
              errors={errors.surname ?? []}
              touchOverride={touch}
            />
          </InputRow>

          <InputRow>
            <TextInput
              id="user-details-email"
              value={email}
              onChange={(v) => { setEmail(v); }}
              label="Email address"
              disabled={!isCreating}
              errors={errors.emailAddress ?? []}
              required
              touchOverride={touch}
            />
            <TextInput
              id="user-details-phone"
              value={telephoneNumber}
              onChange={(v) => { setTelephoneNumber(v); }}
              label="Telephone number"
              disabled={!isCreating && isLocked}
              mutateValue={(v) => {
                const input = v?.replace(/[^\d+]/g, '');
                const plus = input?.substring(0, 1) === '+' ? '+' : '';
                const numbers = input?.substring(plus ? 1 : 0).replace(/\D/g, '');
                return `${plus}${numbers}`;
              }}
              touchOverride={touch}
              errors={errors.telephoneNumber ?? []}
            />
          </InputRow>
          <BreakLine />
          <InputRow>
            <Flex>
              <AdminLabel>Admin</AdminLabel>
              <ToggleInput
                id="user-details-admin"
                value={admin}
                onChange={(v: any) => { setAdmin(v); }}
                disabled={!isCreating && isLocked}
              />
            </Flex>
            <EmptyInput />
          </InputRow>
        </InputGroup>

        {(!isCreating && isEditMode) && (
          <GroupButtons>
            <SecondaryButton small onClick={onCancel}>Cancel</SecondaryButton>
            <Button small onClick={onSave} disabled={!ableToSave}>Save</Button>
          </GroupButtons>
        )}
      </BrokerUserWrapper>

      {isCreating && <VerifyBtn disabled={!canVerify} small onClick={onVerify}>Verify</VerifyBtn>}
    </BrokerUserContainer>
  );
}
